import {
  ApplicationEnum,
  LegalizationHistoryTypeEnum,
  SettlementDetailsApplicationBase,
  VehicleIncidentTypeEnum,
  VehicleTypeEnum,
} from '@/services/api-clients';
import {ApplicationsList} from '@/enums/applications.list';

export function copy<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export function getApplicationName(application: ApplicationEnum) {
  try {
    return ApplicationsList.filter((x) => x.key === application)[0].value;
  } catch (error) {
    return 'Wrong application';
  }
}

export function getProperty<T, K extends keyof T>(o: T, propertyName: K): T[K] {
  return o[propertyName];
}

export function calculateSettlementApplicationSum(model: SettlementDetailsApplicationBase) {
  return (model.applicationQuota
    - model.commission
    - model.promotion
    + model.tips
    + model.netBonus
    - model.minus
    - model.remainingTax);
}

export function legalizationHistoryTypeColor(type: LegalizationHistoryTypeEnum) {
  switch (type) {
    case LegalizationHistoryTypeEnum.ConversationWithClient: return 'blue';
    case LegalizationHistoryTypeEnum.DocumentsSubmission: return 'grey';
    case LegalizationHistoryTypeEnum.OfficeAnswer: return 'blue-grey';
    case LegalizationHistoryTypeEnum.Appeal: return 'teal';
    case LegalizationHistoryTypeEnum.Repeal: return 'teal';
    case LegalizationHistoryTypeEnum.Refusal: return 'red';
    case LegalizationHistoryTypeEnum.OfficeDecision: return 'purple darken-1';
    case LegalizationHistoryTypeEnum.Resignation: return 'grey darken-4';
    case LegalizationHistoryTypeEnum.Others: return 'amber';
    default: return 'primary';
  }
}

export function getVehicleIncidentColor(type: VehicleIncidentTypeEnum) {
  switch (type) {
    case VehicleIncidentTypeEnum.Crash: return 'red';
    case VehicleIncidentTypeEnum.Fee: return 'blue';
    case VehicleIncidentTypeEnum.Maintain: return 'blue-grey';
    case VehicleIncidentTypeEnum.Failure: return 'orange';
    case VehicleIncidentTypeEnum.Other: return 'black';
    case VehicleIncidentTypeEnum.Convey: return 'blue accent-1';
    case VehicleIncidentTypeEnum.Return: return 'blue accent-1';
    case VehicleIncidentTypeEnum.TownTransfer: return 'green';
    case VehicleIncidentTypeEnum.Theft: return 'brown darken-2';
    case VehicleIncidentTypeEnum.BackToFleet: return 'purple';
    case VehicleIncidentTypeEnum.PeriodicVehicleInspection: return 'lime darken-1';
    default: return '';
  }
}

export function canPreviewFile(fileName: string) {
  if (!fileName) return false;

  const fileNameToLowerCase = fileName.toLowerCase();
  return fileNameToLowerCase.endsWith('.pdf')
    || fileNameToLowerCase.endsWith('.jpg')
    || fileNameToLowerCase.endsWith('.jpeg')
    || fileNameToLowerCase.endsWith('.png');
}

export function increaseColor(increase: number): string {
  if (increase > 0) return 'green';

  if (increase < 0) return 'red';

  return '';
}

export function increaseIcon(increase: number): string {
  if (increase > 0) return 'mdi-chevron-double-up';

  if (increase < 0) return 'mdi-chevron-double-down';

  return 'mdi-minus';
}

export function displayVehicleTypeIcon(type: VehicleTypeEnum): string {
  switch (type) {
    case VehicleTypeEnum.Bike: return 'mdi-bike';
    case VehicleTypeEnum.ElectricBike: return 'mdi-bicycle-electric';
    case VehicleTypeEnum.Car: return 'mdi-car-hatchback';
    case VehicleTypeEnum.Motor: return 'mdi-motorbike';
    default: return '';
  }
}

export function hexToRgbA(hex: string, alpha: string){
  let c: any;
  if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)){
    c= hex.substring(1).split('');
    if(c.length === 3){
      c= [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c= '0x'+c.join('');
    return 'rgba('+[(c>>16)&255, (c>>8)&255, c&255].join(',')+','+alpha+')';
  }
  throw new Error('Bad Hex');
}

export function getEnumNumberValues<T>(o: object): T[] {
  return Object.keys(o)
    .map((v) => parseInt(v))
    .filter((v) => !isNaN(v)) as any[] as T[];
}

export function getBlobUrl(file: string | Blob, contentType?: string): string {
  let blob: Blob | null = null;

  if (!(file instanceof Blob)) {
    const binaryArray = atob(file);

    const byteNumbers = new Array(binaryArray.length);
    for (let i = 0; i < binaryArray.length; i++) {
      byteNumbers[i] = binaryArray.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    blob = new Blob([byteArray], { type: contentType });
  } else {
    blob = file;
  }

  return URL.createObjectURL(blob);
}


export type Prettify<T> = {
  [K in keyof T]: T[K]
} & {}

// Used before auto-scrolling a chat: content appended below must not yank the view down while
// someone is reading older messages further up.
export function isScrolledNearBottom(element: HTMLElement, threshold = 120): boolean {
  return element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
}
